import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SupplierFactorOrigin } from './supplierFactorContracts';

dayjs.extend(utc);

// Maps the operator emissions modal's submit `data` (see
// Installations/components/InstallationRealEmissionsModal.jsx handleSubmit) onto
// the free-supplier factor publish/correct contracts. This is the ONLY place the
// S5 contract mismatches are reconciled:
//   D-1  SEFA is REQUIRED by the publish schema — the reused modal makes it
//        optional, so we reject a submit without SEFA before it reaches the wire.
//   D-2  correct addresses a SupplierFactorRevision id (currentRevisionId), not
//        the EmissionsFactor _id the modal edits by — the caller plumbs it in.
//   D-3  the modal collects inclusive date/lastRecordDate; the contract interval
//        is half-open. endExclusive = lastRecordDate + 1 day (day granularity),
//        matching the backend storing endExclusive - 1ms as lastRecordDate.
//   D-4  the reused modal leaves CN code / product / methods / period editable
//        even in "edit" (correct) mode — the correct body always carries
//        seriesKey + interval (same mapping publish uses) so an identity edit
//        isn't silently dropped; the backend decides in-place-correction vs.
//        Factor Successor (D22) from whether they actually changed.

// notifyError (Common/ui/toast) reads err.response.data.{resource,
// action,message}. Shaping the error like an axios failure lets the reused modal
// surface a meaningful message through its existing catch → notifyError path,
// while keeping the form open with the entered values intact.
export class SupplierFactorValidationError extends Error {
  constructor(message, { resource = 'Emissions data', action = 'publish' } = {}) {
    super(message);
    this.name = 'SupplierFactorValidationError';
    this.response = { status: 422, data: { message, resource, action } };
  }
}

const SEFA_REQUIRED_MESSAGE =
  'SEFA is required to publish. Enter the total SEFA, CBAM factor and reporting year before saving.';

const startInclusiveIso = (date) => {
  if (!date || !dayjs.utc(date).isValid()) {
    throw new SupplierFactorValidationError('A valid start date is required to publish.');
  }
  return dayjs.utc(date).startOf('day').toISOString();
};

// Inclusive lastRecordDate -> half-open endExclusive (+1 day at day granularity).
export const toExclusiveEndIso = (lastRecordDate) => {
  if (!lastRecordDate || !dayjs.utc(lastRecordDate).isValid()) {
    throw new SupplierFactorValidationError('A valid last-record date is required to publish.');
  }
  return dayjs.utc(lastRecordDate).startOf('day').add(1, 'day').toISOString();
};

const seriesKeyFromModalData = (data) => {
  const cnCode = typeof data.cnGood === 'object' ? data.cnGood?.cnCode : data.cnGood;
  return {
    installationId: String(data.installationId || ''),
    cnCode: String(cnCode ?? ''),
    productVariant: data.operatorGoodName || '',
    productionMethodCodes: (data.productionMethods || [])
      .map((method) => method.methodCode)
      .filter(Boolean),
  };
};

const emissionsFromModalData = (data) => {
  const ef = data.emissionFactors || {};
  return { direct: Number(ef.direct), indirect: Number(ef.indirect), total: Number(ef.total) };
};

// The modal's indirect/electricity breakdown (data.indirectMeta + top-level
// shareDefaultEmissions) -> the contract `indirectDetail` block (S14/S16). Only
// meaningful values are carried; strings are trimmed so a whitespace-only entry
// doesn't hit the backend `.trim().min(1)`. An empty object is a valid "no
// breakdown" (replacement semantics: it clears, matching the summary-only case).
const finiteNum = (v) =>
  v !== '' && v != null && Number.isFinite(Number(v)) ? Number(v) : undefined;

const indirectDetailFromModalData = (data) => {
  const meta = data.indirectMeta || {};
  const detail = {};
  if (finiteNum(meta.electricityConsumed) != null)
    detail.electricityConsumed = finiteNum(meta.electricityConsumed);
  if (finiteNum(meta.indirectEmissionFactor) != null)
    detail.indirectEmissionFactor = finiteNum(meta.indirectEmissionFactor);
  if (meta.electricitySource) detail.electricitySource = meta.electricitySource;
  if (meta.indirectEmissionFactorSource)
    detail.indirectEmissionFactorSource = meta.indirectEmissionFactorSource;
  const sourceValue = (meta.indirectEmissionFactorSourceValue || '').trim();
  if (sourceValue) detail.indirectEmissionFactorSourceValue = sourceValue;
  const otherSource = (meta.otherSourceIndication || '').trim();
  if (otherSource) detail.otherSourceIndication = otherSource;
  if (meta.determinationType) detail.determinationType = meta.determinationType;
  if (finiteNum(data.shareDefaultEmissions) != null)
    detail.shareDefaultEmissions = finiteNum(data.shareDefaultEmissions);
  // Undefined (not `{}`) when there's no breakdown, so callers OMIT the key
  // rather than send an empty object — create/publish have no prior breakdown,
  // and on correct an omitted key keeps the prior rather than clearing it.
  return Object.keys(detail).length ? detail : undefined;
};

// The publish schema's sefa omits production/precursor (correction sefa shape).
const sefaFromModalData = (data) => {
  const sefa = data.sefa;
  if (!sefa) throw new SupplierFactorValidationError(SEFA_REQUIRED_MESSAGE);
  const total = Number(sefa.totalSefa);
  const cbamFactor = Number(sefa.cbamFactor);
  const reportingYear = Number(sefa.year);
  if (!Number.isFinite(total) || !Number.isFinite(cbamFactor) || !Number.isInteger(reportingYear)) {
    throw new SupplierFactorValidationError(SEFA_REQUIRED_MESSAGE);
  }
  const mapped = { total, cbamFactor, reportingYear };
  if (sefa.benchmark != null && Number.isFinite(Number(sefa.benchmark))) {
    mapped.benchmark = Number(sefa.benchmark);
  }
  if (sefa.benchmarkSource) mapped.benchmarkSource = sefa.benchmarkSource;
  return mapped;
};

// data -> POST /supplier/drafts body. Reuses the exact same seriesKey/interval/
// emissions/SEFA extraction the publish mapper uses (SupplierEmissionsModal
// routes both from the SAME modal submit `data`, differing only in which
// endpoint the "Save as draft" vs "Publish now" choice targets — S10
// draft-by-default), so any drift between the two stays impossible by
// construction. Two real shape differences from the publish body:
//   - the draft contract nests cbamFactor/reportingYear directly under
//     proposedFactor (not inside authoritativeSefa), and requires
//     authoritativeSefa.production/precursor as explicit nulls (never
//     omitted) — see supplierDraftContracts.js createSupplierFactorDraftRequestV1Schema.
//   - the key is `targetSeriesKey`, not `seriesKey`.
export function emissionModalDataToDraftCreateBody(data) {
  const { total, benchmark, benchmarkSource, cbamFactor, reportingYear } = sefaFromModalData(data);
  return {
    version: 1,
    targetSeriesKey: seriesKeyFromModalData(data),
    interval: {
      startInclusive: startInclusiveIso(data.date),
      endExclusive: toExclusiveEndIso(data.lastRecordDate),
    },
    proposedFactor: {
      emissions: emissionsFromModalData(data),
      authoritativeSefa: {
        total,
        ...(benchmark != null ? { benchmark } : {}),
        ...(benchmarkSource ? { benchmarkSource } : {}),
        production: null,
        precursor: null,
      },
      ...(indirectDetailFromModalData(data)
        ? { indirectDetail: indirectDetailFromModalData(data) }
        : {}),
      cbamFactor,
      reportingYear,
    },
  };
}

// data -> POST /supplier/factors/publish body. Throws SupplierFactorValidationError
// (axios-shaped) when SEFA or the interval dates are missing.
export function emissionModalDataToPublishBody(data) {
  return {
    version: 1,
    seriesKey: seriesKeyFromModalData(data),
    interval: {
      startInclusive: startInclusiveIso(data.date),
      endExclusive: toExclusiveEndIso(data.lastRecordDate),
    },
    emissions: emissionsFromModalData(data),
    sefa: sefaFromModalData(data),
    ...(indirectDetailFromModalData(data)
      ? { indirectDetail: indirectDetailFromModalData(data) }
      : {}),
    origin: SupplierFactorOrigin.Direct,
  };
}

// data + currentRevisionId -> POST /supplier/factors/correct body. Sends
// emissions AND sefa (both mandatory in this UI), PLUS seriesKey + interval —
// built with the exact same mapping the publish path uses (A1/D-4). The
// reused edit modal leaves CN code, product, production methods and dates
// editable, so the correct payload must always carry the modal's current
// values for them: the backend treats unchanged identity as an in-place
// correction and changed identity as a Factor Successor (D22) — the frontend
// never decides which; it just always sends what the form holds.
export function emissionModalDataToCorrectBody(data, currentRevisionId) {
  if (!currentRevisionId) {
    throw new SupplierFactorValidationError(
      'This factor cannot be corrected — its current revision is unknown. Reload and try again.',
      { action: 'correct' },
    );
  }
  return {
    version: 1,
    currentRevisionId: String(currentRevisionId),
    seriesKey: seriesKeyFromModalData(data),
    interval: {
      startInclusive: startInclusiveIso(data.date),
      endExclusive: toExclusiveEndIso(data.lastRecordDate),
    },
    emissions: emissionsFromModalData(data),
    sefa: sefaFromModalData(data),
    ...(indirectDetailFromModalData(data)
      ? { indirectDetail: indirectDetailFromModalData(data) }
      : {}),
  };
}
